import type { CheckboxChangeEvent } from 'antd/lib/checkbox/Checkbox'

import { Checkbox, CheckboxSize } from '@sok/design-system'
import { useCallback } from 'react'
import styled from 'styled-components'

interface CheckboxGroupProps<T extends string> {
  value: T[]
  options: { label: string; value: T }[]
  onChange: (updatedValue: T[]) => void
  name?: string
  className?: string
}

export const CheckboxGroup = <T extends string>({
  value,
  options,
  onChange,
  className,
  name,
}: CheckboxGroupProps<T>) => {
  const handleChange = useCallback(
    (targetValue: T) => (e: CheckboxChangeEvent) => {
      e.target.checked
        ? onChange([...value, targetValue])
        : onChange(value.filter((x) => x !== targetValue))
    },
    [value, onChange],
  )

  return (
    <StyledList data-test-id="checkbox-group" className={className}>
      {options.map(({ label, value: optionValue }, i) => {
        const checked = !!value.find((x) => x === optionValue)
        return (
          <li key={i}>
            <Checkbox
              data-test-id="checkbox-group-checkbox"
              name={name}
              size={CheckboxSize.SMALL}
              checked={checked}
              onChange={handleChange(optionValue)}
              value={typeof optionValue === 'string' ? optionValue : undefined}
            >
              {label}
            </Checkbox>
          </li>
        )
      })}
    </StyledList>
  )
}

const StyledList = styled.ol(({ theme }) => ({
  listStyle: 'none',

  li: {
    alignItems: 'center',
    padding: `${theme.spacings.xxSmall} ${theme.spacings.medium}`,

    label: {
      width: '100%',
      display: 'flex',
      flexDirection: 'row',

      '> span:nth-child(2)': {
        fontSize: '1rem',
        flex: 1,
        padding: 0,
      },

      '> span:first-child': {
        order: 2,
      },
    },
  },
}))
